// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import {
  X, ChevronDown, ChevronUp, Copy, Check, Pencil, Plus, Trash2,
  Save, Eye, Code2, GripVertical, RotateCcw, Library,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import CallScripts from '@/components/dialer/CallScripts';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/api-client';
import { isLibraryScript } from '@/components/dialer/callScriptLibrary';
import { useDialerData } from '@/contexts/DialerDataContext';
import { useDialerAuth } from '@/contexts/DialerAuthContext';
import { useDialerUI } from '@/contexts/DialerUIContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useConfirmDialog } from '@/components/shared/ConfirmDialog';

// ─── Defaults & types ───────────────────────────────────────────────────────

// Colors are fixed per type; the display label is resolved from the catalog
// at render time (see `useSectionTypes` in the component below).
const SECTION_TYPE_STYLE = {
  ouverture:     { accent: '#3b82f6', tint: 'rgba(59,130,246,0.10)' },
  proposition:   { accent: '#8b5cf6', tint: 'rgba(139,92,246,0.10)' },
  qualification: { accent: '#f59e0b', tint: 'rgba(245,158,11,0.12)' },
  objection:     { accent: '#ef4444', tint: 'rgba(239,68,68,0.10)' },
  closing:       { accent: '#10b981', tint: 'rgba(16,185,129,0.10)' },
  custom:        { accent: '#6b7280', tint: 'rgba(107,114,128,0.10)' },
};

const uid = () => Math.random().toString(36).slice(2, 10);

// Not translated: this is the default CONTENT of an editable CallerScript
// document (title AND content are serialized verbatim into `content` on
// save, see `saveMutation`). It is the call pitch itself, written in the
// language the agent calls prospects in, independently of the interface
// language; only the surrounding chrome (buttons, hints) follows i18n.
// i18n-exempt-start: default content of the editable call script (see above)
const DEFAULT_SECTIONS = [
  {
    id: uid(), key: 'ouverture', type: 'ouverture', title: 'Ouverture',
    items: [
      { id: uid(), title: 'Accroche standard', content: "Bonjour [Prenom], c'est [Agent] de [Entreprise]. Je vous appelle car [raison personnalisée]. Vous avez 2 minutes ?" },
    ],
  },
  {
    id: uid(), key: 'proposition', type: 'proposition', title: 'Propositions de valeur',
    items: [
      { id: uid(), title: 'Bénéfice #1', content: "Nous aidons les entreprises comme [Societe] à [bénéfice principal]. Nos clients constatent en moyenne [résultat chiffré]." },
      { id: uid(), title: 'Bénéfice #2', content: "Concrètement, nous vous faisons gagner [X heures / X €] par mois sur [processus]." },
    ],
  },
  {
    id: uid(), key: 'qualification', type: 'qualification', title: 'Questions de qualification',
    items: [
      { id: uid(), title: 'Situation actuelle', content: "Comment gérez-vous [problème] aujourd'hui ?" },
      { id: uid(), title: 'Challenge principal', content: "Quel est votre principal challenge sur [sujet] ?" },
      { id: uid(), title: 'Décideurs', content: "Qui d'autre est impliqué dans ce type de décision ?" },
    ],
  },
  {
    id: uid(), key: 'objections', type: 'objection', title: 'Objections',
    items: [
      { id: uid(), title: "C'est trop cher",           content: "Nos clients récupèrent leur investissement en 3 mois en moyenne. Combien vous coûte aujourd'hui [problème] ?" },
      { id: uid(), title: "On a déjà une solution",    content: "Qu'est-ce qui vous manque avec votre solution actuelle ?" },
      { id: uid(), title: "Pas le bon moment",          content: "Quel serait le bon timing pour en reparler ?" },
      { id: uid(), title: "Envoyez un email",           content: "Bien sûr — pour cibler, quel est votre plus gros challenge sur [sujet] ?" },
      { id: uid(), title: "Je dois en parler à mon manager", content: "Si on organisait un call à 3 directement ?" },
    ],
  },
  {
    id: uid(), key: 'closing', type: 'closing', title: 'Closing',
    items: [
      { id: uid(), title: 'Demande de RDV', content: "Est-ce que [jour] ou [jour] vous conviendrait pour une démo de 20 minutes ? Je vous envoie l'invitation tout de suite." },
    ],
  },
];
// i18n-exempt-end

// Backward-compat: the previous shape was [{key, title, content}] (one block per section).
function normalizeSections(raw) {
  if (!Array.isArray(raw)) return DEFAULT_SECTIONS;
  return raw.map((s) => {
    if (Array.isArray(s.items)) {
      return {
        id: s.id || uid(),
        key: s.key || uid(),
        type: s.type || inferType(s.key, s.title),
        title: s.title || 'Section',
        items: s.items.map((it) => ({ id: it.id || uid(), title: it.title || '', content: it.content || '' })),
      };
    }
    // Legacy single-block section → wrap in one item
    return {
      id: uid(),
      key: s.key || uid(),
      type: inferType(s.key, s.title),
      title: s.title || 'Section',
      items: [{ id: uid(), title: '', content: s.content || '' }],
    };
  });
}

function inferType(key = '', title = '') {
  const k = `${key} ${title}`.toLowerCase();
  if (k.includes('ouvert') || k.includes('accroche')) return 'ouverture';
  if (k.includes('propos') || k.includes('valeur') || k.includes('pitch')) return 'proposition';
  if (k.includes('qualif') || k.includes('question') || k.includes('découv')) return 'qualification';
  if (k.includes('object')) return 'objection';
  if (k.includes('clos')) return 'closing';
  return 'custom';
}

// ─── Variable interpolation ─────────────────────────────────────────────────

function buildVars({ prospect, caller }) {
  const fullName = prospect?.prospect_name || '';
  const [first, ...rest] = fullName.split(/\s+/);
  return {
    Prenom:     first || '',
    Nom:        rest.join(' ') || '',
    NomComplet: fullName,
    Societe:    prospect?.prospect_company || prospect?.company || '',
    Telephone:  prospect?.prospect_phone || '',
    Email:      prospect?.prospect_email || '',
    Agent:      caller?.caller_name || caller?.name || (caller?.caller_email || '').split('@')[0] || '',
    Entreprise: caller?.company_name || caller?.client_company || '',
  };
}

function interpolate(text, vars) {
  if (!text) return '';
  return text.replace(/\[([A-Za-zÀ-ÿ_][A-Za-zÀ-ÿ0-9_ ]*)\]/g, (m, key) => {
    const trimmed = key.trim();
    return Object.prototype.hasOwnProperty.call(vars, trimmed) ? (vars[trimmed] || m) : m;
  });
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function ScriptPanel({ onClose }) {
  const { t } = useTranslation(['dialer', 'common']);
  const [ask, confirmDialog] = useConfirmDialog();
  // W4 (B2-tools): the script library left the Outils page; it opens from
  // here, next to the pitch it feeds.
  const [libraryOpen, setLibraryOpen] = useState(false);
  const sectionTypeOptions = useMemo(() => Object.keys(SECTION_TYPE_STYLE).map((key) => ({
    key,
    label: t(`dialer:scriptPanel.types.${key}`),
  })), [t]);
  const { callerData } = useDialerAuth();
  const { connectedContact, selectedProspect } = useDialerUI();
  const { selectedCustomerId } = useDialerData();
  const queryClient = useQueryClient();
  const clientEmail = callerData?.client_email;

  const [editing, setEditing] = useState(false);
  const [previewMode, setPreviewMode] = useState(true); // true = interpolated, false = raw
  const [expanded, setExpanded] = useState({});
  const [copied, setCopied] = useState(null);
  const [draft, setDraft] = useState(null); // sections being edited
  const [docId, setDocId] = useState(null); // id of the existing CallerScript doc (if any)

  // ADR-024 phase 2 — script is per-Customer. Filter on (client_email, customer_id)
  // so each B2B customer has its own pitch. Query key includes customer_id so a
  // sidebar switch invalidates and refetches.
  const { data: scriptDocs, isLoading } = useQuery({
    queryKey: ['caller-script-doc', clientEmail, selectedCustomerId],
    queryFn: () => api.entities.CallerScript.filter({
      customer_id: selectedCustomerId || '',
    }),
    enabled: !!clientEmail,
    staleTime: 60_000,
  });

  // Resolve sections from DB doc (or defaults)
  const sections = useMemo(() => {
    // Library scripts (CallScripts, AIAssistant) share the entity: skip them.
    const docs = (scriptDocs || []).filter((d) => !isLibraryScript(d));
    if (docs.length === 0) return DEFAULT_SECTIONS;
    // Prefer the doc whose title === 'panel' (our reserved slot), else first
    const doc = docs.find((d) => d.title === 'panel') || docs[0];
    try {
      const parsed = JSON.parse(doc.content || '[]');
      return normalizeSections(parsed);
    } catch {
      // Legacy plain-text fallback
      return [{ id: uid(), key: 'legacy', type: 'custom', title: doc.title || 'Script', items: [{ id: uid(), title: '', content: doc.content || '' }] }];
    }
  }, [scriptDocs]);

  // Track docId
  useEffect(() => {
    if (!scriptDocs) return;
    const docs = scriptDocs.filter((d) => !isLibraryScript(d));
    const doc = docs.find((d) => d.title === 'panel') || docs[0];
    setDocId(doc?.id || null);
  }, [scriptDocs]);

  // Default-expand est geree par computeIsOpen (2 premieres sections par defaut)
  // plus besoin d'effet qui ecrivait dans expanded — ca creait un conflit avec
  // collapseAll/toggle.

  // Variable substitution
  const vars = useMemo(
    () => buildVars({ prospect: connectedContact || selectedProspect, caller: callerData }),
    [connectedContact, selectedProspect, callerData],
  );

  // Mutations
  const saveMutation = useMutation({
    mutationFn: async (nextSections) => {
      const payload = {
        // ADR-024 phase 2 — bind script to the active Customer.
        customer_id: selectedCustomerId || '',
        agent_email: callerData?.caller_email || '',
        title: 'panel',
        content: JSON.stringify(nextSections),
        script_type: 'conversational',
        is_ai_generated: false,
      };
      if (docId) return api.entities.CallerScript.update(docId, payload);
      return api.entities.CallerScript.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caller-script-doc', clientEmail, selectedCustomerId] });
      toast.success(t('dialer:scriptPanel.toast.saved'));
      setEditing(false);
      setDraft(null);
    },
    onError: () => toast.error(t('dialer:scriptPanel.toast.saveError')),
  });

  // ── Edit handlers ─────────────────────────────────────────────────────────
  const startEdit = () => { setDraft(structuredClone(sections)); setEditing(true); };
  const cancelEdit = () => { setDraft(null); setEditing(false); };

  const updateSection = (sid, patch) => {
    setDraft((d) => d.map((s) => (s.id === sid ? { ...s, ...patch } : s)));
  };
  const updateItem = (sid, iid, patch) => {
    setDraft((d) => d.map((s) => s.id === sid
      ? { ...s, items: s.items.map((it) => (it.id === iid ? { ...it, ...patch } : it)) }
      : s));
  };
  const addItem = (sid) => {
    setDraft((d) => d.map((s) => s.id === sid
      ? { ...s, items: [...s.items, { id: uid(), title: '', content: '' }] }
      : s));
  };
  const removeItem = (sid, iid) => {
    setDraft((d) => d.map((s) => s.id === sid
      ? { ...s, items: s.items.filter((it) => it.id !== iid) }
      : s));
  };
  const addSection = () => {
    setDraft((d) => [...d, {
      id: uid(), key: uid(), type: 'custom', title: t('dialer:scriptPanel.newSectionTitle'),
      items: [{ id: uid(), title: '', content: '' }],
    }]);
  };
  const removeSection = (sid) => {
    setDraft((d) => d.filter((s) => s.id !== sid));
  };
  const moveSection = (sid, dir) => {
    setDraft((d) => {
      const idx = d.findIndex((s) => s.id === sid);
      if (idx < 0) return d;
      const target = idx + dir;
      if (target < 0 || target >= d.length) return d;
      const next = [...d];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };
  const resetToDefaults = async () => {
    if (!(await ask({ title: t('dialer:scriptPanel.confirmReset'), confirmLabel: t('common:actions.confirm'), tone: 'default' }))) return;
    setDraft(structuredClone(DEFAULT_SECTIONS));
  };

  // isOpenFor unifie le calcul d'etat ouvert/ferme pour une section :
  // - si une valeur explicite existe dans expanded → on la prend
  // - sinon, 2 premieres sections ouvertes par defaut (comportement initial)
  const computeIsOpen = (sid, expandedMap) => {
    if (sid in expandedMap) return !!expandedMap[sid];
    const list = editing ? draft : sections;
    const idx = list.findIndex((s) => s.id === sid);
    return idx >= 0 && idx < 2;
  };
  const isOpenFor = (sid) => computeIsOpen(sid, expanded);
  // Toggle utilise la valeur a jour depuis le setter pour eviter les closures stale
  const toggle = (sid) => setExpanded((e) => ({ ...e, [sid]: !computeIsOpen(sid, e) }));
  const expandAll = () => setExpanded(Object.fromEntries((editing ? draft : sections).map((s) => [s.id, true])));
  const collapseAll = () => setExpanded(Object.fromEntries((editing ? draft : sections).map((s) => [s.id, false])));

  const handleCopy = (id, text) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  const visible = editing ? draft : sections;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className="flex flex-col flex-1 overflow-hidden border-l"
      style={{ background: 'var(--vf-surface-1)', borderColor: 'var(--vf-border)' }}
    >
      {confirmDialog}
      <Dialog open={libraryOpen} onOpenChange={setLibraryOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('dialer:scriptPanel.library.title')}</DialogTitle>
          </DialogHeader>
          <CallScripts clientEmail={clientEmail} />
        </DialogContent>
      </Dialog>
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0"
        style={{ borderColor: 'var(--vf-border)' }}
      >
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-semibold" style={{ color: 'var(--vf-text-1)' }}>{t('dialer:scriptPanel.header.title')}</span>
          {!editing && (
            <button
              onClick={() => setPreviewMode((v) => !v)}
              title={previewMode ? t('dialer:scriptPanel.header.showRawVars') : t('dialer:scriptPanel.header.showPreview')}
              className="flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-vf-sm"
              style={{ color: 'var(--vf-text-3)', background: 'var(--vf-surface-2)' }}
            >
              {previewMode ? <Eye size={10} /> : <Code2 size={10} />}
              {previewMode ? t('dialer:scriptPanel.header.preview') : t('dialer:scriptPanel.header.raw')}
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!editing ? (
            <>
              <IconBtn title={t('dialer:scriptPanel.library.open')} onClick={() => setLibraryOpen(true)}><Library size={13} /></IconBtn>
              <IconBtn title={t('dialer:scriptPanel.actions.expandAll')} onClick={expandAll}><ChevronDown size={13} /></IconBtn>
              <IconBtn title={t('dialer:scriptPanel.actions.collapseAll')} onClick={collapseAll}><ChevronUp size={13} /></IconBtn>
              <IconBtn title={t('dialer:scriptPanel.actions.edit')} onClick={startEdit}><Pencil size={13} /></IconBtn>
            </>
          ) : (
            <>
              <IconBtn title={t('dialer:scriptPanel.actions.reset')} onClick={resetToDefaults}><RotateCcw size={13} /></IconBtn>
              <button
                onClick={cancelEdit}
                className="text-[11px] font-medium px-2 py-1 rounded-vf-sm"
                style={{ color: 'var(--vf-text-3)' }}
              >
                {t('common:actions.cancel')}
              </button>
              <button
                onClick={() => saveMutation.mutate(draft)}
                disabled={saveMutation.isPending || !clientEmail}
                className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-vf-sm"
                style={{ background: 'var(--vf-blue, #3b82f6)', color: 'white', opacity: saveMutation.isPending ? 0.6 : 1 }}
              >
                <Save size={11} />
                {t('common:actions.save')}
              </button>
            </>
          )}
          {onClose && (
            <IconBtn title={t('common:actions.close')} onClick={onClose}><X size={14} /></IconBtn>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {isLoading && !sections.length ? (
          <div className="text-[12px] text-center py-8" style={{ color: 'var(--vf-text-3)' }}>{t('common:actions.loading')}</div>
        ) : (
          <>
            {visible.map((section, idx) => {
              const meta = SECTION_TYPE_STYLE[section.type] || SECTION_TYPE_STYLE.custom;
              const isOpen = isOpenFor(section.id);
              const num = String(idx + 1).padStart(2, '0');

              return (
                <div
                  key={section.id}
                  className="mb-2 rounded-vf-lg border overflow-hidden"
                  style={{
                    background: 'var(--vf-surface-2)',
                    borderColor: 'var(--vf-border)',
                    borderLeft: `3px solid ${meta.accent}`,
                  }}
                >
                  {/* Section header */}
                  <div
                    className="flex items-center justify-between px-3 py-2"
                    style={{ background: meta.tint }}
                  >
                    <button
                      onClick={() => toggle(section.id)}
                      className="flex items-center gap-2 flex-1 text-left"
                      style={{ color: 'var(--vf-text-1)' }}
                    >
                      <span className="vf-font-mono text-[10px] font-bold" style={{ color: meta.accent }}>{num}</span>
                      {editing ? (
                        <input
                          value={section.title}
                          onChange={(e) => updateSection(section.id, { title: e.target.value })}
                          onClick={(e) => e.stopPropagation()}
                          className="text-[12px] font-semibold uppercase tracking-wider bg-transparent border-b focus:outline-none flex-1"
                          style={{ color: 'var(--vf-text-1)', borderColor: 'var(--vf-border)' }}
                        />
                      ) : (
                        <span className="text-[12px] font-semibold uppercase tracking-wider">{section.title}</span>
                      )}
                      <span
                        className="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded"
                        style={{ background: meta.accent, color: 'white' }}
                      >
                        {section.items.length}
                      </span>
                    </button>
                    {editing && (
                      <div className="flex items-center gap-1 ml-2">
                        <select
                          value={section.type}
                          onChange={(e) => updateSection(section.id, { type: e.target.value })}
                          className="text-[10px] px-1 py-0.5 rounded border"
                          style={{ background: 'var(--vf-surface-1)', color: 'var(--vf-text-1)', borderColor: 'var(--vf-border)' }}
                        >
                          {sectionTypeOptions.map(({ key, label }) => (
                            <option key={key} value={key}>{label}</option>
                          ))}
                        </select>
                        <IconBtn title={t('dialer:scriptPanel.actions.moveUp')} onClick={() => moveSection(section.id, -1)}><ChevronUp size={12} /></IconBtn>
                        <IconBtn title={t('dialer:scriptPanel.actions.moveDown')} onClick={() => moveSection(section.id, +1)}><ChevronDown size={12} /></IconBtn>
                        <IconBtn title={t('dialer:scriptPanel.actions.deleteSection')} onClick={() => removeSection(section.id)}><Trash2 size={12} /></IconBtn>
                      </div>
                    )}
                    {!editing && (
                      <button
                        onClick={() => toggle(section.id)}
                        className="ml-2 flex items-center justify-center"
                        style={{ width: 20, height: 20, color: 'var(--vf-text-3)' }}
                      >
                        {isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                      </button>
                    )}
                  </div>

                  {/* Items */}
                  {isOpen && (
                    <div className="px-2.5 py-2 space-y-2">
                      {section.items.map((item, iidx) => {
                        const rawText = item.content || '';
                        const display = !editing && previewMode ? interpolate(rawText, vars) : rawText;
                        const itemKey = `${section.id}:${item.id}`;
                        return (
                          <div
                            key={item.id}
                            className="rounded-vf border overflow-hidden"
                            style={{ background: 'var(--vf-surface-1)', borderColor: 'var(--vf-border)' }}
                          >
                            <div
                              className="flex items-center justify-between px-2.5 py-1.5"
                              style={{ background: meta.tint }}
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span
                                  className="vf-font-mono text-[9px] font-bold rounded px-1 py-0.5"
                                  style={{ background: meta.accent, color: 'white' }}
                                >
                                  {num}.{iidx + 1}
                                </span>
                                {editing ? (
                                  <input
                                    value={item.title || ''}
                                    onChange={(e) => updateItem(section.id, item.id, { title: e.target.value })}
                                    placeholder={t('dialer:scriptPanel.titlePlaceholder')}
                                    className="text-[11px] font-semibold bg-transparent border-b focus:outline-none flex-1"
                                    style={{ color: 'var(--vf-text-1)', borderColor: 'var(--vf-border)' }}
                                  />
                                ) : item.title ? (
                                  <span className="text-[11px] font-semibold truncate" style={{ color: 'var(--vf-text-1)' }}>
                                    {item.title}
                                  </span>
                                ) : null}
                              </div>
                              <div className="flex items-center gap-0.5">
                                {!editing ? (
                                  <button
                                    onClick={() => handleCopy(itemKey, display)}
                                    className="flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-vf-sm"
                                    style={{ color: copied === itemKey ? meta.accent : 'var(--vf-text-3)' }}
                                  >
                                    {copied === itemKey ? <Check size={10} /> : <Copy size={10} />}
                                    {copied === itemKey ? t('common:actions.copied') : t('common:actions.copy')}
                                  </button>
                                ) : (
                                  <IconBtn title={t('common:actions.delete')} onClick={() => removeItem(section.id, item.id)}>
                                    <Trash2 size={11} />
                                  </IconBtn>
                                )}
                              </div>
                            </div>
                            <div className="px-2.5 py-2">
                              {editing ? (
                                <textarea
                                  value={item.content || ''}
                                  onChange={(e) => updateItem(section.id, item.id, { content: e.target.value })}
                                  rows={Math.max(2, (item.content || '').split('\n').length)}
                                  placeholder={t('dialer:scriptPanel.contentPlaceholder')}
                                  className="w-full text-[12px] leading-relaxed bg-transparent focus:outline-none resize-y"
                                  style={{ color: 'var(--vf-text-1)' }}
                                />
                              ) : (
                                <div
                                  className="text-[12px] whitespace-pre-wrap leading-relaxed"
                                  style={{ color: 'var(--vf-text-1)' }}
                                >
                                  {display}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {editing && (
                        <button
                          onClick={() => addItem(section.id)}
                          className="w-full flex items-center justify-center gap-1 text-[11px] font-medium py-1.5 rounded-vf border border-dashed transition-colors"
                          style={{ color: meta.accent, borderColor: meta.accent }}
                        >
                          <Plus size={12} />
                          {t('dialer:scriptPanel.actions.addItem')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {editing && (
              <button
                onClick={addSection}
                className="w-full flex items-center justify-center gap-1 text-[11px] font-semibold py-2 mt-1 rounded-vf-lg border border-dashed"
                style={{ color: 'var(--vf-text-2)', borderColor: 'var(--vf-border)' }}
              >
                <Plus size={13} />
                {t('dialer:scriptPanel.actions.addSection')}
              </button>
            )}

            {/* Variables hint while editing. The bracketed tokens below are
                the interpolation variable names matched against `content`
                (see `buildVars`/`interpolate`): they must stay as written,
                never translated, or they stop matching the script text. */}
            {editing && (
              <div
                className="mt-3 px-3 py-2 rounded-vf text-[10px] leading-relaxed"
                style={{ background: 'var(--vf-surface-2)', color: 'var(--vf-text-3)', border: '1px dashed var(--vf-border)' }}
              >
                <span className="font-semibold" style={{ color: 'var(--vf-text-2)' }}>{t('dialer:scriptPanel.variablesLabel')}</span>
                {/* i18n-exempt: placeholder tokens the script engine substitutes, not prose */}
                [Prenom] · [Nom] · [NomComplet] · [Societe] · [Telephone] · [Email] · [Agent] · [Entreprise]
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Small helper ───────────────────────────────────────────────────────────
function IconBtn({ title, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className="flex items-center justify-center rounded-vf-sm"
      style={{ width: 24, height: 24, color: 'var(--vf-text-3)' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--vf-surface-3)'; e.currentTarget.style.color = 'var(--vf-text-1)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--vf-text-3)'; }}
    >
      {children}
    </button>
  );
}
